import React, { useState } from "react";
import { Form, Formik } from "formik";
import FormField from "../components/forms/FormField";
import StyleModal from "../components/StyleSelector/StyleModal";
import PreviewSection from "../components/StoryPreview/PreviewSection";
import TutorialSlideshow from "../components/TutorialSlideshow/TutorialSlideshow";
import { bookStyles, visualStyles, writingStyles } from "../data/styleOptions";
import { createPageValidationSchema } from "../validation/createPageSchema";
import { createPageInitialValues } from "../constants/formValues";
import StyleOptions from "./CreatePage/components/StyleOptions";
import PageOptions from "./CreatePage/components/PageOptions";
import SpeechToText from "../components/SpeechToText/SpeechToText";
import { useCreatePage } from "./CreatePage/hooks/useCreatePage";

export default function CreatePage() {
  const [activeModal, setActiveModal] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const {
    showPreview,
    currentPage,
    pageContents,
    selectedStyles,
    isProcessing,
    handleStyleSelect,
    handleGeneratePreview,
    handlePageChange,
    handleContentChange,
    handleSaveAndContinue,
    wordsPerPage,
  } = useCreatePage();

  return (
    <>
      <div className="flex items-center justify-between mb-4 px-1">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Story</h1>
          <p className="text-gray-600">Write your story and choose styles.</p>
        </div>
        <button
          onClick={() => setShowTutorialModal(true)}
          className="px-4 py-2 rounded-md bg-yellow-100 text-yellow-900 hover:bg-yellow-200 font-medium"
        >
          Tutorial
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-full overflow-hidden">
        <Formik
          initialValues={createPageInitialValues}
          validationSchema={createPageValidationSchema}
          onSubmit={handleSaveAndContinue}
          validateOnChange={true}
          validateOnBlur={true}
          validateOnMount={true}
        >
          {({ values, setFieldValue, handleChange, handleBlur, errors, touched, setTouched }) => (
            <>
              <div className="bg-white p-6 rounded-lg shadow-md overflow-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Create Your Story 
                </h2>

                <Form className="space-y-6">
                  <FormField
                    label="Story Title"
                    name="title"
                    type="text"
                    placeholder="Enter your story title"
                    options={[]}
                  />

                  <StyleOptions
                    selectedStyles={selectedStyles}
                    onStyleSelect={handleStyleSelect}
                    onModalOpen={setActiveModal}
                    validationErrors={validationErrors}
                  />

                  <PageOptions
                    wordsPerPage={wordsPerPage}
                    onPageChange={handlePageChange}
                    onContentChange={handleContentChange}
                    isWordsPerPageDisabled={true}
                  />

                  <FormField
                    label="Story Content"
                    name="storyContent"
                    type="textarea"
                    rows={6}
                    placeholder="Type or paste your story here..."
                    options={[]}
                    labelAction={
                      <SpeechToText
                        onTranscriptionComplete={(text) => setFieldValue('storyContent', text)}
                      />
                    }
                  />

                  <button
                    type="button"
                    onClick={() => {
                      // Check values directly
                      const errors = {};
                      if (!values.title?.trim()) errors.title = 'Title is required';
                      if (!values.storyContent?.trim()) errors.storyContent = 'Story content is required';
                      if (!selectedStyles.bookStyle) errors.bookStyle = 'Book style is required';
                      if (!selectedStyles.visualStyle) errors.visualStyle = 'Visual style is required';
                      if (!selectedStyles.writingStyle) errors.writingStyle = 'Writing style is required';
                      
                      if (Object.keys(errors).length > 0) {
                        setValidationErrors(errors);
                        setTouched({
                          title: true,
                          storyContent: true,
                          bookStyle: true,
                          visualStyle: true,
                          writingStyle: true,
                        });
                        return;
                      }
                      
                      setValidationErrors({});
                      handleGeneratePreview(values);
                    }}
                    disabled={isProcessing}
                    className={`w-full py-2 px-4 rounded-md transition-colors font-medium flex items-center justify-center gap-2 ${
                      isProcessing
                        ? 'bg-indigo-400 text-white cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {isProcessing && (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    )}
                    {isProcessing ? 'Generating Story Preview...' : 'Generate Preview'}
                  </button>
                </Form>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md overflow-auto">
                {showPreview ? (
                  <PreviewSection
                    currentPage={currentPage}
                    totalPages={parseInt(values.totalPages, 10)}
                    pageContents={pageContents}
                    onPageChange={handlePageChange}
                    onContentChange={handleContentChange}
                    onSavePreview={() => handleSaveAndContinue(values)}
                    onSaveBasicInfo={() => handleSaveAndContinue(values)}
                    onContinueWithoutSave={() => handleSaveAndContinue(values)}
                    bookId={values.bookId}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-500">
                      Your story preview will appear here after generation
                    </p>
                  </div>
                )}
              </div>

              {/* Style Selection Modals */}
              <StyleModal
                isOpen={activeModal === "book"}
                onClose={() => setActiveModal(null)}
                title="Select Book Style"
                description="Choose a style that best fits your story"
                options={bookStyles}
                selectedOption={selectedStyles.bookStyle}
                onSelect={(style) => {
                  handleStyleSelect("bookStyle", style);
                  setActiveModal(null);
                }}
              />

              <StyleModal
                isOpen={activeModal === "visual"}
                onClose={() => setActiveModal(null)}
                title="Select Visual Style"
                description="Choose how your story will look"
                options={visualStyles}
                selectedOption={selectedStyles.visualStyle}
                onSelect={(style) => {
                  handleStyleSelect("visualStyle", style);
                  setActiveModal(null);
                }}
              />

              <StyleModal
                isOpen={activeModal === "writing"}
                onClose={() => setActiveModal(null)}
                title="Select Writing Style"
                description="Choose your story's tone and style"
                options={writingStyles}
                selectedOption={selectedStyles.writingStyle}
                onSelect={(style) => {
                  handleStyleSelect("writingStyle", style);
                  setActiveModal(null);
                }}
              />
            </>
          )}
        </Formik>
      </div>

      {/* Tutorial Modal */}
      {showTutorialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">Tutorial</h2>
              <button
                onClick={() => setShowTutorialModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                aria-label="Close tutorial"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4">
              <TutorialSlideshow />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
